import { useMemo } from 'react';
import { type ColumnElementType, type Table } from '../types';
import { columnElementsExistenceCheckMap } from '../widget/constants';

type InfoKeys =
  | ColumnElementType
  | 'affix'
  | 'customColor'
  | 'discount'
  | 'highlight'
  | 'oldPrice'
  | 'priceCaption'
  | 'form';
type PropertiesInfo = Record<InfoKeys, boolean>;

const pricingModelsWithAffix = ['from', 'subscription', 'per'];

export const useTablesHaveProperty = (tables?: Table | Table[]) => {
  return useMemo(() => {
    const initialValue: PropertiesInfo = {
      button: false,
      title: false,
      features: false,
      picture: false,
      price: false,
      oldPrice: false,
      priceCaption: false,
      discount: false,
      highlight: false,
      customColor: false,
      affix: false,
      form: false
    };
    if (!tables) {
      return initialValue;
    }

    const flatColumns = Array.isArray(tables)
      ? tables.flatMap(({ columns }) => columns)
      : tables.columns;

    return flatColumns.reduce(
      (
        {
          affix: previousHasAffix,
          button: previousHasButton,
          customColor: previousHasCustomColor,
          discount: previousHasDiscount,
          features: previousHasFeatures,
          highlight: previousHasHighlight,
          oldPrice: previousHasOldPrice,
          picture: previousHasPicture,
          price: previousHasPrice,
          priceCaption: previousHasPriceCaption,
          title: previousHasTitle,
          form: previousHasForm
        },
        column
      ) => {
        const {
          button: hasButton,
          features: hasFeatures,
          picture: hasPicture,
          price: hasPrice,
          title: hasTitle
        } = columnElementsExistenceCheckMap;

        const currentHasPrice = previousHasPrice || hasPrice(column);

        const {
          discount: { customLabel, discountType, enabled: discountEnabled },
          highlightLabel,
          oldPrice: { customPrice, enabled: oldPriceEnabled },
          price: { pricingModel },
          priceCaption,
          primaryColor,
          buttonAction
        } = column;

        const hasOldPrice =
          oldPriceEnabled && (pricingModel !== 'custom' || !!customPrice);
        const hasDiscount =
          discountEnabled && (discountType !== 'custom' || !!customLabel);

        return {
          button: previousHasButton || hasButton(column),
          title: previousHasTitle || hasTitle(column),
          features: previousHasFeatures || hasFeatures(column),
          picture: previousHasPicture || hasPicture(column),
          price: currentHasPrice,
          oldPrice: previousHasOldPrice || (currentHasPrice && hasOldPrice),
          priceCaption:
            previousHasPriceCaption || (currentHasPrice && !!priceCaption),
          discount: previousHasDiscount || (currentHasPrice && hasDiscount),
          highlight: previousHasHighlight || !!highlightLabel,
          customColor:
            previousHasCustomColor ||
            (!!primaryColor && primaryColor !== 'transparent'),
          affix:
            previousHasAffix || pricingModelsWithAffix.includes(pricingModel),
          form: previousHasForm || buttonAction === 'openForm'
        };
      },
      initialValue
    );
  }, [tables]);
};
